import React, { useState, useEffect } from 'react';
import { Flex, RadioGroup, Radio, Stack, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import { Button, Menu, MenuButton, MenuList } from '@morressier/alembic';

export interface Journal {
  value: string;
  label: string;
}

interface JournalMenuProps {
  appliedJournal: Journal | undefined;
  options: Journal[];
  onApply: (selected: Journal) => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const JournalMenu: React.FC<JournalMenuProps> = ({
  appliedJournal,
  options,
  onApply,
  hasNextPage = false,
  isLoadingMore = false,
  onLoadMore
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  useEffect(() => {
    const idx = options.findIndex(f => f.value === appliedJournal?.value);
    setSelectedIndex(idx >= 0 ? idx : 0);
  }, [appliedJournal, options]);

  return (
    <Menu closeOnSelect={false} ariaLabel="Journal-menu">
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            variant="secondary"
            leftIcon={<FontAwesomeIcon icon={faSliders} />}
            width={{ base: '100%', md: 'auto' }}
            maxWidth="100%"
            justifyContent="flex-start"
            minWidth={0}
            overflow="hidden"
          >
            <Text
              as="span"
              display="block"
              flex="1"
              minWidth={0}
              textAlign="left"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              Journal: {appliedJournal?.label}
            </Text>
          </MenuButton>

          <MenuList
            minWidth={{ base: 'calc(100vw - 1.5rem)', md: '240px' }}
            maxWidth={{ base: 'calc(100vw - 1.5rem)', md: '360px' }}
            maxHeight={{ base: '60vh', md: '70vh' }}
            overflowY="auto"
            p={{ base: 2, md: 3 }}
          >
            <RadioGroup
              onChange={val => {
                const selected = options[Number(val)];
                if (selected) {
                  setSelectedIndex(Number(val));
                  onApply(selected);
                  onClose();
                }
              }}
              value={String(selectedIndex)}
            >
              <Stack spacing={3}>
                {options.map((field, index) => (
                  <Radio key={index} value={String(index)}>
                    <Text
                      textStyle="textBody"
                      whiteSpace="normal"
                      overflowWrap="anywhere"
                    >
                      {field.label}
                    </Text>
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            {hasNextPage && onLoadMore && (
              <Flex justifyContent="center" pt={3}>
                <Button
                  variant="secondary"
                  size="sm"
                  width="100%"
                  isDisabled={isLoadingMore}
                  isLoading={isLoadingMore}
                  // Keep focus inside the menu: disabling the button while it
                  // holds focus would blur it and close the menu mid-fetch.
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onLoadMore()}
                >
                  Load More
                </Button>
              </Flex>
            )}
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export default JournalMenu;
